import { useMemo, useState, memo, useRef } from "react";
import { Link } from "react-router-dom";
import { routes } from "@/common/utils/routes";
import { IGif, IUser } from "@/common/services/types";
import { metricsBus } from "@/common/buses";
import { CurrentUserObject } from "@/common/hooks/useVisitorInfo";
import PlusIcon from "@/common/images/icons/plusIcon.svg?react";
import CheckIcon from "./images/icons/tick.svg?react";
import DefaultUserIcon from "@/common/images/icons/default_userpic.svg?react";
import { className } from "@/common/utils";
import { useWindowSize } from "usehooks-ts";
import "./UserInfo.scss";
import { VerifiedIcon } from "components/UI/VerifiedIcon";

interface IUserInfoProps {
  gif: IGif;
  user: IUser;
  rel?: string;
  isFollowing: boolean;
  currentUser: CurrentUserObject | null;
  onProfileMetric: (value: string) => void;
  onFollow: () => void;
}

const TIMESTAMP_TO_TIME = 1000;

export const UserInfo: React.FC<IUserInfoProps> = memo(({
  gif,
  user,
  currentUser,
  rel,
  isFollowing,
  onFollow,
  onProfileMetric
}) => {
  const [showMore, setShowMore] = useState(false);
  const { width: windowWidth } = useWindowSize();

  const descriptionRef = useRef<HTMLDivElement>(null);
  const descriptionTruncated = useMemo(() => {
    if (!descriptionRef.current) {
      return false;
    }

    return descriptionRef.current.scrollWidth > descriptionRef.current.clientWidth;
  }, [windowWidth]);

  const isCurrentUserGif = currentUser?.username === user.username;
  const createDate = new Date(gif.createDate * TIMESTAMP_TO_TIME)
    .toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  const gifDescription = useMemo(() => gif.description || user.description, [user, gif]);

  const handleUserClick = () => {
    onProfileMetric(user.username);
    metricsBus.emit("watchPageNavigation", { elementType: "Creator", targetURL: routes.user(user.username) });
  };

  const renderShowMoreButton = () => {
    if (!descriptionTruncated && !showMore) {
      return null;
    }

    return (
      <button
        onClick={() => setShowMore(prev => !prev)}
        aria-label={`show ${showMore ? "less" : " more"} button`}
      >{showMore ? "Less" : " More"}</button>
    );
  };

  return (
    <div className="UserInfo">
      <div className="UserInfo-ImageWrap">
        <Link
          to={routes.user(user.username)}
          onClick={handleUserClick}
          aria-label={`Link to ${user.username} profile`}
        >
          {user.profileImageUrl ? <img loading="lazy" src={user.profileImageUrl} alt={`Poster for ${user.username}`}/> : <DefaultUserIcon />}
        </Link>
      </div>
      {!isCurrentUserGif && (
        <button className="UserInfo-FollowBtn" title={user.username} onClick={onFollow} disabled={isFollowing} aria-label="follow button">
          {isFollowing ? <CheckIcon /> : <PlusIcon />}
        </button>
      )}
      <div className="UserInfo-Text">
        <Link className="UserInfo-Date" to={routes.watch(gif.id, rel)}>{createDate}</Link>
        <Link className="UserInfo-UserLink" to={routes.user(user.username)} onClick={handleUserClick}>
          <span>{user.name || user.username}</span>
          {user.verified && <VerifiedIcon isStudio={user.studio} title={`${user.username} is a verified RedGIFs ${user.studio ? "Studio" : "creator"}`} />}
        </Link>
      </div>
      {gifDescription && (
        <p className="UserInfo-Description">
          <span ref={descriptionRef} className={className({ "descriptionText": true, "fullText": showMore })}>
            {gifDescription}
          </span>
          {renderShowMoreButton()}
        </p>
      )}
    </div>
  );
});
